/**
 * Community Feed — feed (with FitTip injection) + create + like + comment
 * + edit + delete + report + photo capture/gallery + workout cards.
 * Top tabs: Feed / Local / Live / Challenges / Tips.
 */
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Heart, MessageCircle, MoreHorizontal, Send, Award, X, Flag, ShoppingBag,
  Camera, Image as ImageIcon, Edit2, Trash2, ShieldCheck,
} from "lucide-react";
import TopBar from "../components/layout/TopBar";
import BottomNav from "../components/layout/BottomNav";
import { apiClient } from "../services/api";
import { useAuthStore } from "../store/authStore";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import FitTipCard from "../components/community/FitTipCard";
import WorkoutCard from "../components/community/WorkoutCard";
import LocalSquad from "./community/LocalSquad";
import LiveSessions from "./community/LiveSessions";
import ChallengesAndDuels from "./community/ChallengesAndDuels";
import { compressImage } from "../utils/imageCompress";

const TABS = [
  { id: "feed", label: "Feed" },
  { id: "local", label: "Local" },
  { id: "live", label: "Live" },
  { id: "challenges", label: "Challenges" },
  { id: "tips", label: "Tips" },
];

export default function Community() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [items, setItems] = useState([]); // mixed posts + fittips
  const [composer, setComposer] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [imageViewer, setImageViewer] = useState(null);

  const load = async () => {
    if (!user) {
      // unauthed: simple posts list
      try {
        const { data } = await apiClient.get("/feed/posts?limit=30");
        setItems((data || []).map((p) => ({ ...p, _kind: "post" })));
      } catch { /* noop */ }
      return;
    }
    try {
      const { data } = await apiClient.get("/feed/personalized?limit=20");
      setItems(data?.items || []);
    } catch {
      // fall back to posts
      try {
        const { data } = await apiClient.get("/feed/posts?limit=30");
        setItems((data || []).map((p) => ({ ...p, _kind: "post" })));
      } catch { /* noop */ }
    }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (id) => {
    if (id === "tips") {
      navigate("/community/tips");
      return;
    }
    if (id === "local" || id === "live" || id === "challenges") {
      // Sessions 2-4 placeholder
      setTab(id);
      return;
    }
    setTab(id);
  };

  const togglLike = async (post) => {
    if (!user) return toast("Sign in to like posts");
    try {
      const { data } = await apiClient.post(`/feed/posts/${post.post_id}/like`);
      setItems((prev) =>
        prev.map((p) =>
          p.post_id === post.post_id
            ? { ...p, liked_by_me: data.liked, likes_count: (p.likes_count || 0) + (data.liked ? 1 : -1) }
            : p,
        ),
      );
    } catch {
      toast.error("Could not update like");
    }
  };

  const reportPost = async (post, reason, detail_text) => {
    try {
      await apiClient.post(`/community/report`, {
        content_type: "post",
        content_id: post.post_id,
        reason,
        detail_text: detail_text || null,
      });
      toast.success("Thanks. Our team will review this.");
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "already_reported") toast.info("You already reported this.");
      else if (err === "cannot_report_own") toast.error("You can't report your own post.");
      else if (err === "rate_limited") toast.error("Daily report limit reached (20).");
      else toast.error("Could not report");
    }
  };

  const deletePost = async (post) => {
    try {
      await apiClient.delete(`/feed/posts/${post.post_id}`);
      setItems((prev) => prev.filter((p) => p.post_id !== post.post_id));
      toast.success("Post deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="app-page" data-testid="community-page">
      <TopBar title="Community" />

      {/* Top tabs */}
      <div
        className="sticky top-[56px] z-20"
        style={{
          background: "var(--bg-elevated, #F0EEEA)",
          borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))",
        }}
      >
        <div
          className="max-w-2xl mx-auto px-4 flex gap-4 overflow-x-auto items-center"
          data-testid="community-tabs"
          style={{ scrollbarWidth: "none" }}
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              data-testid={`community-tab-${t.id}`}
              className="py-3 whitespace-nowrap"
              style={{
                fontFamily: "Barlow, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                color: tab === t.id ? "var(--text-primary, #0C0C0B)" : "var(--text-tertiary, #8A8884)",
                borderBottom: tab === t.id ? "2px solid var(--accent, #2A2825)" : "2px solid transparent",
                background: "transparent",
              }}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => navigate("/community/rules")}
            data-testid="community-rules-icon"
            title="Community Rules"
            aria-label="Community Rules"
            className="ml-auto grid place-items-center"
            style={{ width: 34, height: 34, borderRadius: 999, color: "var(--text-secondary,#4A4845)" }}
          >
            <ShieldCheck size={18} />
          </button>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pt-3 pb-32">
        {tab === "feed" && (
          items.length === 0 ? (
            <EmptyFeed />
          ) : (
            <div className="space-y-3">
              {items.map((item, i) =>
                item._kind === "fittip" ? (
                  <FitTipCard key={`tip-${item.tip_id}-${i}`} tip={item} onChange={load} />
                ) : (
                  <PostCard
                    key={item.post_id}
                    post={item}
                    isOwn={user && item.user_id === user.user_id}
                    onLike={() => togglLike(item)}
                    onEdit={() => setEditTarget(item)}
                    onDelete={() => deletePost(item)}
                    onReport={(reason, detail) => reportPost(item, reason, detail)}
                    onImageClick={(url) => setImageViewer(url)}
                  />
                ),
              )}
            </div>
          )
        )}
        {tab !== "feed" && tab !== "tips" && (
          tab === "local" ? <LocalSquad />
          : tab === "live" ? <LiveSessions />
          : tab === "challenges" ? <ChallengesAndDuels />
          : <ComingSoonStub tab={tab} />
        )}
      </main>

      {user && (
        <button
          onClick={() => setComposer(true)}
          data-testid="community-post-fab"
          className="fixed right-5 bottom-24 z-30 w-14 h-14 rounded-full grid place-items-center"
          style={{ background: "#00D4FF", color: "#000", boxShadow: "0 8px 22px rgba(0,212,255,0.45)" }}
          aria-label="Post"
        >
          <Plus size={22} strokeWidth={2.5} />
        </button>
      )}

      <BottomNav />

      <AnimatePresence>
        {composer && (
          <Composer
            onClose={() => setComposer(false)}
            onPosted={() => {
              setComposer(false);
              load();
            }}
          />
        )}
        {editTarget && (
          <Composer
            editing={editTarget}
            onClose={() => setEditTarget(null)}
            onPosted={() => {
              setEditTarget(null);
              load();
            }}
          />
        )}
        {imageViewer && (
          <ImageViewer src={imageViewer} onClose={() => setImageViewer(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="text-center py-14 px-6">
      <div className="font-display text-[22px]">Quiet around here</div>
      <p className="text-[14px] text-ink-soft mt-1">
        Be the first to share something with your community.
      </p>
    </div>
  );
}

function ComingSoonStub({ tab }) {
  const titles = {
    local: { title: "Local Squad", sub: "Hyperlocal fitness groups in your neighbourhood. Coming next." },
    live: { title: "Trainer Live Sessions", sub: "Join live trainer-led classes. Coming soon." },
    challenges: { title: "Challenges & Duels", sub: "Streak challenges + 1v1 duels. Coming soon." },
  };
  const t = titles[tab] || titles.local;
  return (
    <div className="text-center py-16 px-6">
      <div className="font-display text-[26px]">{t.title}</div>
      <p className="text-[13px] text-ink-soft mt-2 max-w-sm mx-auto">{t.sub}</p>
    </div>
  );
}

function PostCard({ post, isOwn, onLike, onEdit, onDelete, onReport, onImageClick }) {
  const user = useAuthStore((s) => s.user);
  const [menu, setMenu] = useState(false);
  const [reportSheet, setReportSheet] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [comments, setComments] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");

  const loadComments = async () => {
    if (comments) return setShowComments(true);
    try {
      const { data } = await apiClient.get(`/feed/posts/${post.post_id}/comments`);
      setComments(data || []);
      setShowComments(true);
    } catch {
      toast.error("Could not load comments");
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      const { data } = await apiClient.post(`/feed/posts/${post.post_id}/comments`, { text: newComment.trim() });
      setComments((prev) => [...(prev || []), data]);
      setNewComment("");
    } catch {
      toast.error("Could not comment");
    }
  };

  const photo = post.image_url || post.attached_image_b64;
  const isWorkoutCard = post.post_type === "workout_card" && post.workout_card_data;

  return (
    <div
      className="relative bg-white rounded-[16px] p-4 border border-line2"
      data-testid={`feed-post-${post.post_id}`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-full grid place-items-center text-[12px] font-medium"
          style={{ background: "var(--accent-blue-dim)", color: "var(--text-primary)" }}
        >
          {(post.author_name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-medium truncate">{post.author_name}</span>
            {post.is_verified_pro && (
              <span
                title="Verified professional"
                style={{
                  background: "#00D4FF", color: "#000", fontSize: 9, fontWeight: 800,
                  padding: "1px 7px", borderRadius: 100, letterSpacing: "0.08em",
                }}
              >
                {(post.verified_role || "PRO").toUpperCase()}
              </span>
            )}
          </div>
          <div className="text-[11px] text-ink-soft">
            {timeAgo(post.created_at)} · {post.hub === "all" ? "All FitFrik" : post.hub}
            {post.edited_at && (
              <span data-testid={`post-edited-${post.post_id}`} style={{ marginLeft: 6, color: "var(--text-tertiary, #8A8884)" }}>
                · Edited
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setMenu((v) => !v)}
          aria-label="More"
          data-testid={`post-menu-${post.post_id}`}
          className="w-8 h-8 grid place-items-center rounded-full"
        >
          <MoreHorizontal size={16} className="text-ink-soft" />
        </button>
      </div>

      {/* Workout card inline */}
      {isWorkoutCard && (
        <div className="mt-3 flex justify-center">
          <WorkoutCard
            session={post.workout_card_data}
            user={{ name: post.author_name, city: post.hub === "all" ? null : post.hub }}
            scale={0.78}
          />
        </div>
      )}

      {/* Text */}
      <p className="text-[14px] mt-3 whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>
        {post.text}
      </p>
      {photo && !isWorkoutCard && (
        <button
          type="button"
          onClick={() => onImageClick(photo)}
          data-testid={`post-image-${post.post_id}`}
          className="mt-3 block w-full p-0 border-0 bg-transparent"
        >
          <img
            src={photo}
            alt=""
            className="w-full rounded-[12px] border border-line2"
            style={{ maxHeight: 300, objectFit: "cover" }}
          />
        </button>
      )}
      {post.attached_product && <PostProductCard product={post.attached_product} />}
      {post.attached_fitpoints != null && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-pill"
          style={{ background: "#FFF1D1", color: "#A05E00", fontSize: 12, fontWeight: 600, borderRadius: 100 }}
        >
          <Award size={13} /> +{post.attached_fitpoints} FitPoints
        </div>
      )}

      <div className="flex items-center gap-5 mt-3 pt-3 border-t border-line2">
        <button
          onClick={onLike}
          data-testid={`post-like-${post.post_id}`}
          className="flex items-center gap-1.5"
          style={{ color: post.liked_by_me ? "#00D4FF" : "var(--text-tertiary)", fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}
        >
          <Heart size={16} fill={post.liked_by_me ? "#00D4FF" : "none"} strokeWidth={post.liked_by_me ? 0 : 2} />
          {post.likes_count || 0}
        </button>
        <button
          onClick={loadComments}
          data-testid={`post-comment-${post.post_id}`}
          className="flex items-center gap-1.5 text-ink-soft"
          style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}
        >
          <MessageCircle size={16} />
          {post.comments_count || 0}
        </button>
      </div>

      {showComments && (
        <div className="mt-3 pt-3 border-t border-line2">
          {(comments || []).map((c) => (
            <CommentRow
              key={c.comment_id}
              comment={c}
              isOwn={user && c.user_id === user.user_id}
              onEdited={(updated) => setComments((cs) => cs.map((x) => x.comment_id === updated.comment_id ? updated : x))}
              onDeleted={() => setComments((cs) => cs.filter((x) => x.comment_id !== c.comment_id))}
            />
          ))}
          <div className="flex gap-2 mt-2">
            <input
              type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment…"
              data-testid={`comment-input-${post.post_id}`}
              className="flex-1 h-9 px-3 rounded-pill border border-line bg-canvas2 text-[13px] outline-none"
            />
            <button
              onClick={submitComment} data-testid={`comment-send-${post.post_id}`}
              className="w-9 h-9 rounded-full grid place-items-center"
              style={{ background: "#00D4FF", color: "#000" }}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}

      {menu && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setMenu(false)} />
          <div
            className="absolute z-[56]"
            style={{
              top: 48, right: 12,
              background: "#fff", border: "1px solid #E2E0DC",
              borderRadius: 12, boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              minWidth: 200, overflow: "hidden",
            }}
          >
            {isOwn ? (
              <>
                <button
                  onClick={() => { setMenu(false); onEdit?.(); }}
                  data-testid={`post-edit-${post.post_id}`}
                  className="w-full px-4 py-3 text-left flex items-center gap-2"
                  style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}
                >
                  <Edit2 size={14} /> Edit Post
                </button>
                <button
                  onClick={() => { setMenu(false); setConfirmDelete(true); }}
                  data-testid={`post-delete-${post.post_id}`}
                  className="w-full px-4 py-3 text-left flex items-center gap-2 border-t border-line2"
                  style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600, color: "#A52121" }}
                >
                  <Trash2 size={14} /> Delete Post
                </button>
              </>
            ) : (
              <button
                onClick={() => { setMenu(false); setReportSheet(true); }}
                className="w-full px-4 py-3 text-left flex items-center gap-2"
                style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600, color: "#A52121" }}
                data-testid={`post-report-${post.post_id}`}
              >
                <Flag size={14} /> Report this post
              </button>
            )}
          </div>
        </>
      )}

      {reportSheet && (
        <ReportSheet onCancel={() => setReportSheet(false)} onSubmit={(reason, detail) => { setReportSheet(false); onReport(reason, detail); }} contentTypeLabel="post" />
      )}

      {confirmDelete && (
        <ConfirmDeleteSheet onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); onDelete?.(); }} />
      )}
    </div>
  );
}

function CommentRow({ comment, isOwn, onEdited, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.text);
  const [menu, setMenu] = useState(false);
  const [reportSheet, setReportSheet] = useState(false);

  const save = async () => {
    if (!text.trim() || text.trim() === comment.text) {
      setEditing(false);
      return;
    }
    try {
      const { data } = await apiClient.put(`/community/comments/${comment.comment_id}`, { text: text.trim() });
      onEdited?.(data);
      setEditing(false);
      toast.success("Updated");
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "contact_blocked") {
        toast.error(e.response.data.detail.message || "Contact details not allowed.");
      } else {
        toast.error("Could not save");
      }
    }
  };

  const remove = async () => {
    try {
      await apiClient.delete(`/community/comments/${comment.comment_id}`);
      onDeleted?.();
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const submitReport = async (reason, detail_text) => {
    try {
      await apiClient.post(`/community/report`, {
        content_type: "comment",
        content_id: comment.comment_id,
        reason,
        detail_text: detail_text || null,
      });
      toast.success("Thanks. Our team will review this.");
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "already_reported") toast.info("You already reported this.");
      else if (err === "cannot_report_own") toast.error("You can't report your own comment.");
      else toast.error("Could not report");
    }
  };

  return (
    <div className="py-1.5 relative" data-testid={`comment-${comment.comment_id}`}>
      {editing ? (
        <div className="flex gap-2">
          <input
            data-testid={`comment-edit-input-${comment.comment_id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="flex-1 h-9 px-3 rounded-pill border border-line bg-canvas2 text-[13px] outline-none"
          />
          <button
            data-testid={`comment-edit-save-${comment.comment_id}`}
            onClick={save}
            className="px-3 h-9 rounded-pill"
            style={{ background: "#00D4FF", color: "#000", fontSize: 12, fontWeight: 700 }}
          >Save</button>
          <button
            onClick={() => { setEditing(false); setText(comment.text); }}
            className="px-2 h-9 text-[12px] text-ink-soft"
          >Cancel</button>
        </div>
      ) : (
        <div className="text-[13px] flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <span className="font-medium">{comment.author_name}</span>{" "}
            <span style={{ color: "var(--text-secondary)" }}>{comment.text}</span>
            {comment.edited_at && (
              <span
                data-testid={`comment-edited-${comment.comment_id}`}
                style={{
                  fontFamily: "Barlow, sans-serif",
                  fontSize: 11, fontWeight: 400,
                  color: "var(--text-tertiary, #8A8884)",
                  marginLeft: 6,
                }}
              >
                Edited
              </span>
            )}
          </div>
          <button
            data-testid={`comment-menu-${comment.comment_id}`}
            onClick={() => setMenu((v) => !v)}
            aria-label="Comment options"
            className="shrink-0 w-6 h-6 grid place-items-center text-ink-soft"
          >
            <MoreHorizontal size={14} />
          </button>
        </div>
      )}
      {menu && (
        <div
          className="absolute right-2 z-10"
          style={{
            top: 26, background: "#fff", border: "1px solid var(--border,rgba(12,12,11,0.16))",
            borderRadius: 10, padding: 4, minWidth: 130,
          }}
        >
          {isOwn ? (
            <>
              <button
                data-testid={`comment-edit-${comment.comment_id}`}
                onClick={() => { setMenu(false); setEditing(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px]"
                style={{ background: "transparent", border: "none" }}
              >
                <Edit2 size={13} /> Edit
              </button>
              <button
                data-testid={`comment-delete-${comment.comment_id}`}
                onClick={() => { setMenu(false); remove(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px]"
                style={{ background: "transparent", border: "none", color: "#DC2626" }}
              >
                <Trash2 size={13} /> Delete
              </button>
            </>
          ) : (
            <button
              data-testid={`comment-report-${comment.comment_id}`}
              onClick={() => { setMenu(false); setReportSheet(true); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[13px]"
              style={{ background: "transparent", border: "none", color: "#DC2626" }}
            >
              <Flag size={13} /> Report
            </button>
          )}
        </div>
      )}
      {reportSheet && (
        <ReportSheet
          contentTypeLabel="comment"
          onCancel={() => setReportSheet(false)}
          onSubmit={(reason, detail) => { setReportSheet(false); submitReport(reason, detail); }}
        />
      )}
    </div>
  );
}

function ReportSheet({ onCancel, onSubmit, contentTypeLabel = "post" }) {
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState("");
  const nav = useNavigate();
  const reasons = [
    { id: "spam", label: "Spam or scam" },
    { id: "inappropriate", label: "Inappropriate content" },
    { id: "harassment", label: "Harassment or bullying" },
    { id: "hate_speech", label: "Hate speech" },
    { id: "nudity", label: "Nudity or sexual content" },
    { id: "self_harm", label: "Self-harm or suicide" },
    { id: "impersonation", label: "Impersonation" },
    { id: "misinformation", label: "False or misleading information" },
  ];
  return (
    <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center bg-black/50" data-testid="report-sheet">
      <div className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] p-5 max-h-[85vh] overflow-y-auto">
        <h3 className="font-display text-[22px]">Report {contentTypeLabel}</h3>
        <p className="text-[13px] text-ink-soft mt-1">Why are you reporting this?</p>
        <div className="space-y-2 mt-3">
          {reasons.map((r) => (
            <button
              key={r.id}
              onClick={() => setReason(r.id)}
              data-testid={`report-${r.id}`}
              className="w-full px-4 h-11 rounded-[12px] border text-left text-[14px] font-medium"
              style={{
                borderColor: reason === r.id ? "#00D4FF" : "var(--border, rgba(12,12,11,0.16))",
                background: reason === r.id ? "rgba(0,212,255,0.08)" : "transparent",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        <textarea
          data-testid="report-detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value.slice(0, 200))}
          placeholder="Optional detail (200 chars)…"
          rows={2}
          className="w-full mt-3 p-3 rounded-[12px] border border-line2 text-[13px] outline-none"
        />
        <div className="text-right text-[11px] text-ink-soft mt-1">{detail.length}/200</div>

        <button
          onClick={() => reason && onSubmit(reason, detail || null)}
          disabled={!reason}
          data-testid="report-submit"
          className="w-full mt-2 h-12 rounded-pill disabled:opacity-50"
          style={{ background: "#0C0C0B", color: "#F4F3F1", borderRadius: 100, fontWeight: 700 }}
        >
          Submit report
        </button>
        <button
          onClick={() => { onCancel(); nav("/community/rules"); }}
          data-testid="report-see-rules"
          className="w-full mt-2 h-10 text-[13px] font-medium"
          style={{ color: "var(--text-secondary, #4A4845)", background: "transparent" }}
        >
          See our community rules
        </button>
        <button onClick={onCancel} className="w-full mt-1 h-10 rounded-pill text-[13px] text-ink-soft" style={{ borderRadius: 100 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ConfirmDeleteSheet({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center bg-black/50" data-testid="delete-confirm-sheet">
      <div className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] p-5">
        <h3 style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>
          Delete this post?
        </h3>
        <p style={{ fontFamily: "Barlow", fontSize: 13, color: "var(--text-secondary, #4A4845)", marginTop: 6 }}>
          This cannot be undone.
        </p>
        <button
          onClick={onConfirm}
          data-testid="delete-confirm-btn"
          className="w-full mt-4"
          style={{ height: 48, background: "#EF4444", color: "white", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700, border: "none" }}
        >
          Delete
        </button>
        <button
          onClick={onCancel}
          className="w-full mt-2"
          style={{ height: 44, background: "transparent", color: "var(--text-secondary, #4A4845)", border: "1px solid var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 600 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ImageViewer({ src, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] grid place-items-center"
      style={{ background: "rgba(0,0,0,0.92)" }}
      onClick={onClose}
      data-testid="image-viewer"
    >
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "white", border: "none" }} aria-label="Close">
        <X size={18} />
      </button>
      <img src={src} alt="" style={{ maxWidth: "92%", maxHeight: "92%", objectFit: "contain", borderRadius: 12 }} />
    </motion.div>
  );
}

function Composer({ onClose, onPosted, editing }) {
  const user = useAuthStore((s) => s.user);
  const isEdit = !!editing;
  const isWorkoutCard = editing?.post_type === "workout_card";
  const [text, setText] = useState(editing?.text || "");
  const [hub, setHub] = useState(editing?.hub || (user?.city ? `${user.city}` : "all"));
  const [busy, setBusy] = useState(false);
  const [attachedImage, setAttachedImage] = useState(editing?.image_url || editing?.attached_image_b64 || null);
  const [attachWorkout, setAttachWorkout] = useState(false);
  const [latestSession, setLatestSession] = useState(null);
  const [attachFp, setAttachFp] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!isEdit) {
      apiClient.get("/workout/history?limit=1").then((r) => setLatestSession(r.data?.[0])).catch(() => {});
    }
  }, [isEdit]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    try {
      const compressed = await compressImage(file);
      setAttachedImage(compressed);
    } catch {
      toast.error("Could not load image");
    }
  };

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      if (isEdit) {
        await apiClient.patch(`/feed/posts/${editing.post_id}`, {
          text: text.trim(),
          image_url: isWorkoutCard ? null : attachedImage, // workout card has no separate photo
        });
        toast.success("Saved changes");
      } else {
        await apiClient.post("/feed/posts", {
          text: text.trim(),
          hub,
          image_url: attachedImage,
          workout_session_id: attachWorkout && latestSession ? latestSession.session_id : null,
          attached_fitpoints: attachFp ? user?.fitpoints_balance || 0 : null,
        });
        toast.success("Posted");
      }
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Could not save" : "Could not post"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ duration: 0.25 }}
      className="fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[24px] p-5"
      data-testid="composer-sheet"
      style={{ maxHeight: "92vh", overflowY: "auto" }}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[22px]">{isEdit ? "Edit post" : "New post"}</h3>
        <button onClick={onClose} aria-label="Close" className="w-8 h-8 grid place-items-center rounded-full bg-canvas2">
          <X size={16} />
        </button>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 280))}
        placeholder="Share with your community…"
        data-testid="composer-text"
        className="mt-4 w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        style={{ minHeight: 100 }}
      />
      <div className="text-right text-[11px] text-ink-soft">{text.length}/280</div>

      {/* Photo bar */}
      {!isWorkoutCard && (
        <div className="flex gap-2 mt-2" data-testid="composer-photo-bar">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            data-testid="composer-camera"
            className="flex-1 h-11 rounded-[12px] border border-line2 grid place-items-center"
            style={{ background: "var(--bg-canvas2, #F2F0EC)" }}
          >
            <Camera size={18} />
          </button>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            data-testid="composer-gallery"
            className="flex-1 h-11 rounded-[12px] border border-line2 grid place-items-center"
            style={{ background: "var(--bg-canvas2, #F2F0EC)" }}
          >
            <ImageIcon size={18} />
          </button>
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {attachedImage && !isWorkoutCard && (
        <div className="relative mt-3" data-testid="composer-photo-preview">
          <img
            src={attachedImage}
            alt=""
            style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 12 }}
          />
          <button
            onClick={() => setAttachedImage(null)}
            aria-label="Remove image"
            data-testid="composer-photo-remove"
            className="absolute top-2 right-2 grid place-items-center"
            style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "white", border: "none" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {isWorkoutCard && (
        <div className="mt-3 text-[12px] text-ink-soft">
          Workout stats are part of the workout card and cannot be changed. You can edit the caption above.
        </div>
      )}

      {/* Other attach pills (only when creating new) */}
      {!isEdit && (
        <div className="flex flex-wrap gap-2 mt-3">
          {latestSession && (
            <button
              onClick={() => setAttachWorkout((v) => !v)}
              data-testid="attach-workout"
              className="flex items-center gap-1.5 px-3 h-9 rounded-pill border border-line2 text-[12px]"
              style={{ background: attachWorkout ? "var(--accent-blue-dim)" : "transparent", borderRadius: 100 }}
            >
              Workout · {latestSession.workout_name}
            </button>
          )}
          <button
            onClick={() => setAttachFp((v) => !v)}
            data-testid="attach-fitpoints"
            className="flex items-center gap-1.5 px-3 h-9 rounded-pill border border-line2 text-[12px]"
            style={{ background: attachFp ? "var(--accent-blue-dim)" : "transparent", borderRadius: 100 }}
          >
            <Award size={13} /> FitPoints
          </button>
        </div>
      )}

      {!isEdit && (
        <div className="mt-3 text-[12px] text-ink-soft" data-testid="composer-hub">
          Posting to: <span className="font-medium" style={{ color: "var(--text-primary)" }}>
            {hub === "all" ? "All FitFrik" : `${hub} Hub`}
          </span>
          <select
            value={hub}
            onChange={(e) => setHub(e.target.value)}
            data-testid="composer-hub-select"
            className="ml-2 underline bg-transparent border-0 outline-none"
            style={{ color: "var(--text-primary)", fontSize: 12, cursor: "pointer" }}
          >
            <option value="all">All FitFrik</option>
            {user?.city && <option value={user.city}>{user.city} Hub</option>}
            {["Mumbai", "Bangalore", "Delhi", "Pune", "Hyderabad", "Gurgaon", "Chennai"]
              .filter((c) => c !== user?.city)
              .map((c) => (
                <option key={c} value={c}>{c} Hub</option>
              ))}
          </select>
        </div>
      )}

      <button
        onClick={submit}
        disabled={busy || !text.trim()}
        data-testid="composer-submit"
        className="w-full mt-4 h-12 rounded-pill disabled:opacity-50"
        style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
      >
        {busy ? (isEdit ? "Saving…" : "Posting…") : (isEdit ? "Save Changes" : "Post")}
      </button>
    </motion.div>
  );
}

function PostProductCard({ product }) {
  const navigate = useNavigate();
  if (!product) return null;
  const unavailable = product.status && product.status !== "active";
  return (
    <button
      type="button"
      onClick={() => !unavailable && navigate(`/marketplace/product/${product.product_id}`)}
      data-testid={`post-product-card-${product.product_id}`}
      className="mt-3 w-full text-left bg-white rounded-[12px] border border-line2 overflow-hidden flex items-stretch"
      style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
    >
      <div className="w-24 shrink-0 bg-canvas2">
        {product.photo && (
          <img src={product.photo} alt="" className="w-full h-full object-cover" style={{ aspectRatio: "1/1" }} />
        )}
      </div>
      <div className="flex-1 min-w-0 p-3 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <ShoppingBag size={11} style={{ color: "#00B0D5" }} />
            <span className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: "#00B0D5" }}>From the Shop</span>
          </div>
          <div className="mt-0.5 text-[13px] font-semibold line-clamp-2">{product.title}</div>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="font-barlow-cond font-black text-[16px]">₹{(product.price_inr || 0).toLocaleString("en-IN")}</span>
          <span className="text-[11px] font-bold px-2.5 h-7 inline-flex items-center rounded-pill"
            style={{ background: unavailable ? "#ECEAE7" : "#00D4FF", color: unavailable ? "#6B6760" : "#000", borderRadius: 100 }}>
            {unavailable ? "Unavailable" : "View"}
          </span>
        </div>
      </div>
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
